import React from "react";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import Link from "next/link";
import ListingCard from "components/ClientSearch/Listings/ListingCard";
import { memoize, noop } from "lodash";
import { useCurrentUser } from "hooks";
import { constantCase } from "util/strings";
import Spinner from "components/Spinner";
import { analyticsEvents, track } from "util/analytics";
import { BUILDING_AVAILABILITY_QUERY_buildingOrRedirect_building_listings as Listing } from "./types/BUILDING_AVAILABILITY_QUERY";
import styles from "./mobileStyles.module.scss";

export const BUILDING_AVAILABILITY_QUERY = gql`
  query BUILDING_AVAILABILITY_QUERY($buildingId: ID) {
    buildingOrRedirect(id: $buildingId) {
      building {
        id
        identity
        slug
        listings {
          id
          floorAndSuite
          estimatedSquareFeet
          squareFeet
          maxOccupancy
          estimatedMaxOccupancy
          displayMonthlyPrice
          displayPSF
          accuratePsf
          estimatedPsf
          brokerInsights {
            id
            content
          }
          leaseType
          status
          estimatedMonthlyPrice
          accurateMonthlyPrice
          leaseTerm
          description
          image2 {
            id
            url(size: "other_listing_small")
          }
          coverImage {
            id
            url
          }
          building {
            id
            identity
            lat
            lng
            address
            slug
            city
            state
            zip
            neighborhood {
              id
              externalId
              name
              parameterizedName
            }
            submarket {
              id
              name
              externalId
            }
            market {
              id
              name
              externalId
            }
            hasVirtualTours
          }
        }
        market {
          id
          externalId
        }
        city
        neighborhood {
          id
          externalId
        }
      }
    }
  }
`;

const Loading = () => (
  <>
    <h1>Availability</h1>
    <Spinner />
  </>
);

interface IProps {
  buildingId: string;
  type: "Building" | "Listing";
}

const getLink = memoize((id, slug, uuid, isUserSignedIn) => {
  if (isUserSignedIn) {
    return `/tourbook/listing/${id}`;
  }
  return `/building${slug}/${uuid}?listingId=${id}`;
});

export const AvailabilityMobileView = ({ buildingId, type }: IProps) => {
  const { currentUser } = useCurrentUser();
  const { data, loading } = useQuery(BUILDING_AVAILABILITY_QUERY, {
    variables: { buildingId },
  });

  if (loading) return <Loading />;

  const {
    buildingOrRedirect: { building },
  } = data;
  const signedIn = !!currentUser;
  return (
    <div>
      {building.listings.map((listing: Listing) => (
        <div className={styles.cardLink} key={`listing-${listing.id}`}>
          <Link
            passHref
            href={getLink(
              listing.id,
              building.slug,
              building.identity,
              signedIn
            )}
            data-qa="listing-card-link"
          >
            <a className={styles.listingCardLink}>
              <ListingCard
                showImage={false}
                listing={listing}
                signUpType={type}
                source="Availability Table Inquiry"
                areaExternalIds={
                  building.neighborhood?.externalId
                    ? [building.neighborhood.externalId]
                    : []
                }
                cityOfInterest={constantCase(building.city)}
                marketOfInterest={building?.market?.externalId}
                trackingEvent={() => {
                  track(analyticsEvents.clicked, {
                    category: `${type} Page CTAs`,
                    label: "Availability Table Inquiry",
                  });
                }}
                onSuccess={noop}
              />
            </a>
          </Link>
        </div>
      ))}
      {building.listings.length === 0 && (
        <p>
          There aren&rsquo;t any publicly available listings in this building
          right now. Inquire about this space and we&rsquo;ll help you find the
          right fit!
        </p>
      )}
    </div>
  );
};

export default AvailabilityMobileView;
